import { FRIENDLY_ID } from '@bangle.io/config';
import {
  ASSET_LOCATION_PREFERENCES,
  COLOR_THEMES,
  COMMENT_FONTS,
  EDITOR_FONTS,
  isAssetLocationPreference,
  isColorThemePreference,
  isCommentFontPreference,
  isEditorFontPreference,
  isSettingsRouteInfo,
  SETTINGS_PAGE_DEFINITIONS,
  type SettingsPageId,
} from '@bangle.io/constants';
import { useCoreServices } from '@bangle.io/context';
import type {
  AssetLocationPreference,
  ColorThemePreference,
  CommentFontPreference,
  EditorFontPreference,
} from '@bangle.io/types';
import {
  Button,
  cn,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsPage,
} from '@bangle.io/ui-components';
import { useAtom, useAtomValue } from 'jotai';
import {
  ArrowLeft,
  ArrowLeftRight,
  BriefcaseBusiness,
  Download,
  ExternalLink,
  History,
  type LucideIcon,
  Settings2,
  UserRound,
} from 'lucide-react';
import React from 'react';
import { usePwaInstall } from '../common/use-pwa-install';
import { AppHeader } from '../layout/app-header';
import { PageContentContainer } from '../layout/main-content-container';
import { AccountSettingsPage } from './page-settings-account';
import { ImportExportSettingsPage } from './page-settings-import-export';
import { RecoverySettingsPage } from './page-settings-recovery';
import { WorkspacesSettingsPage } from './page-settings-workspaces';

const ASSET_LOCATION_LABELS: Record<AssetLocationPreference, string> = {
  'assets-folder': t.app.settings.general.assetsFolder,
  adjacent: t.app.settings.general.adjacentToNote,
};

const ASSET_LOCATION_OPTIONS: Array<{
  value: AssetLocationPreference;
  label: string;
}> = ASSET_LOCATION_PREFERENCES.map((value) => ({
  value,
  label: ASSET_LOCATION_LABELS[value],
}));

// Base UI's `Select.Value` renders the raw value while the popup is closed
// unless the Root is given an `items` value→label map to resolve the label.
const ASSET_LOCATION_ITEMS: Record<string, string> = Object.fromEntries(
  ASSET_LOCATION_OPTIONS.map((option) => [option.value, option.label]),
);
const COMMENT_FONT_OPTIONS = COMMENT_FONTS.map((font) => ({
  value: font.id,
  label: font.label,
}));
const COMMENT_FONT_ITEMS: Record<string, string> = Object.fromEntries(
  COMMENT_FONT_OPTIONS.map((option) => [option.value, option.label]),
);
const EDITOR_FONT_OPTIONS = EDITOR_FONTS.map((font) => ({
  value: font.id,
  label: font.label,
}));
const EDITOR_FONT_ITEMS: Record<string, string> = Object.fromEntries(
  EDITOR_FONT_OPTIONS.map((option) => [option.value, option.label]),
);
const EDITOR_FONT_FAMILY: Record<EditorFontPreference, string> = {
  aquifer: "'Aquifer', Georgia, serif",
  courier: "'Courier Prime', monospace",
  plex: "'IBM Plex Mono', monospace",
  libron: "'Libron', Georgia, 'Times New Roman', serif",
  outfit: "'Outfit', ui-sans-serif, system-ui, sans-serif",
  'special-elite': "'Special Elite', cursive",
  'times-new-bastard': "'Times New Bastard', Georgia, serif",
};

// Presentation for each settings page. Keyed by SettingsPageId so adding a page
// to SETTINGS_PAGE_DEFINITIONS (in @bangle.io/constants) forces a matching entry
// here at compile time.
const SETTINGS_PAGE_META: Record<
  SettingsPageId,
  { label: string; icon: LucideIcon; title: string }
> = {
  general: {
    label: t.app.settings.nav.general,
    icon: Settings2,
    title: t.app.settings.general.title,
  },
  account: {
    label: t.app.settings.nav.account,
    icon: UserRound,
    title: t.app.settings.account.title,
  },
  workspaces: {
    label: t.app.settings.nav.workspaces,
    icon: BriefcaseBusiness,
    title: t.app.settings.workspaces.title,
  },
  recovery: {
    label: t.app.settings.nav.recovery,
    icon: History,
    title: t.app.settings.recovery.title,
  },
  'import-export': {
    label: t.app.settings.nav.importExport,
    icon: ArrowLeftRight,
    title: t.app.settings.importExport.title,
  },
};

function hasUnsafeAppHrefCharacter(href: string) {
  return [...href].some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return (
      character === '\\' ||
      codePoint <= 0x1f ||
      (codePoint >= 0x7f && codePoint <= 0x9f)
    );
  });
}

/** @internal Exported from this module only for focused URL-boundary tests. */
export function safeAppHref(
  href: string | undefined,
  currentHref = window.location.href,
) {
  if (!href || hasUnsafeAppHrefCharacter(href)) {
    return undefined;
  }

  try {
    const currentUrl = new URL(currentHref);
    const targetUrl = new URL(href, currentUrl);

    if (
      !href.startsWith('/') ||
      href.startsWith('//') ||
      targetUrl.pathname.startsWith('//') ||
      targetUrl.protocol !== currentUrl.protocol ||
      targetUrl.host !== currentUrl.host
    ) {
      return undefined;
    }

    return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
  } catch {
    return undefined;
  }
}

export function PageSettings() {
  const { navigation } = useCoreServices();
  const routeInfo = useAtomValue(navigation.$routeInfo);
  const activePage =
    SETTINGS_PAGE_DEFINITIONS.find((page) => page.route === routeInfo.route)
      ?.id ?? 'general';

  return <SettingsLayout activePage={activePage} />;
}

function SettingsLayout({ activePage }: { activePage: SettingsPageId }) {
  const { navigation, workbenchState } = useCoreServices();
  const routeInfo = useAtomValue(navigation.$routeInfo);
  const pwaInstall = usePwaInstall();
  const [assetLocationPreference, setAssetLocationPreference] = useAtom(
    workbenchState.$assetLocationPreference,
  );
  const [commentFontPreference, setCommentFontPreference] = useAtom(
    workbenchState.$commentFontPreference,
  );
  const [colorThemePreference, setColorThemePreference] = useAtom(
    workbenchState.$colorThemePreference,
  );
  const [editorFontPreference, setEditorFontPreference] = useAtom(
    workbenchState.$editorFontPreference,
  );
  const returnTo = safeAppHref(
    isSettingsRouteInfo(routeInfo) ? routeInfo.payload.returnTo : undefined,
  );

  const backHref =
    returnTo ??
    navigation.toUri({
      route: 'welcome',
      payload: {},
    });
  const navItems = SETTINGS_PAGE_DEFINITIONS.map((page) => ({
    id: page.id,
    label: SETTINGS_PAGE_META[page.id].label,
    href: navigation.toUri({
      route: page.route,
      payload: { returnTo },
    }),
    icon: SETTINGS_PAGE_META[page.id].icon,
  }));

  return (
    <>
      <AppHeader />
      <PageContentContainer constrainContentWidth={false}>
        <SettingsPage.SettingsPageLayout>
          <SettingsPage.SettingsPageHeader
            backHref={backHref}
            backIcon={<ArrowLeft className="h-4 w-4 shrink-0" />}
            backLabel={t.app.settings.backToApp}
            eyebrow={t.app.settings.title}
            nav={
              <SettingsPage.SettingsPageNav
                activeId={activePage}
                ariaLabel={t.app.settings.title}
                items={navItems}
              />
            }
            title={SETTINGS_PAGE_META[activePage].title}
          />

          <SettingsPage.SettingsPageContent>
            {activePage === 'general' ? (
              <>
                <SettingsPage.SettingsSection
                  title={t.app.settings.general.appSection}
                >
                  <SettingsPage.SettingsRow
                    control={
                      <code
                        className="rounded-md bg-muted px-2.5 py-1 font-mono text-muted-foreground text-sm"
                        data-testid="app-version"
                      >
                        {FRIENDLY_ID}
                      </code>
                    }
                    description={t.app.settings.general.versionDescription}
                    title={t.app.settings.general.versionTitle}
                  />
                  {pwaInstall.canInstall || pwaInstall.isInstalling ? (
                    <SettingsPage.SettingsRow
                      control={
                        <Button
                          data-testid="settings-install-app"
                          disabled={pwaInstall.isInstalling}
                          onClick={() => {
                            void pwaInstall.install();
                          }}
                          type="button"
                        >
                          <Download className="h-4 w-4" />
                          {pwaInstall.isInstalling
                            ? t.app.settings.general.installingPwa
                            : t.app.settings.general.installPwaButton}
                        </Button>
                      }
                      description={t.app.settings.general.installPwaDescription}
                      title={t.app.settings.general.installPwaTitle}
                    />
                  ) : null}
                  {pwaInstall.canOpenInApp ? (
                    <SettingsPage.SettingsRow
                      control={
                        <Button
                          data-testid="settings-open-in-app"
                          onClick={() => {
                            pwaInstall.openInApp();
                          }}
                          type="button"
                        >
                          <ExternalLink className="h-4 w-4" />
                          {t.app.settings.general.openInAppButton}
                        </Button>
                      }
                      description={t.app.settings.general.openInAppDescription}
                      title={t.app.settings.general.openInAppTitle}
                    />
                  ) : null}
                </SettingsPage.SettingsSection>

                <SettingsPage.SettingsSection
                  title={t.app.settings.general.appearanceSection}
                >
                  <SettingsPage.SettingsRow
                    control={
                      <ColorThemePicker
                        onValueChange={setColorThemePreference}
                        value={colorThemePreference}
                      />
                    }
                    title={t.app.settings.general.colorThemeTitle}
                  />
                  <SettingsPage.SettingsRow
                    control={
                      <EditorFontSelect
                        onValueChange={setEditorFontPreference}
                        value={editorFontPreference}
                      />
                    }
                    description={t.app.settings.general.editorFontDescription}
                    title={t.app.settings.general.editorFontTitle}
                  />
                  <SettingsPage.SettingsRow
                    control={
                      <CommentFontSelect
                        onValueChange={setCommentFontPreference}
                        value={commentFontPreference}
                      />
                    }
                    description={t.app.settings.general.commentFontDescription}
                    title={t.app.settings.general.commentFontTitle}
                  />
                </SettingsPage.SettingsSection>

                <SettingsPage.SettingsSection
                  title={t.app.settings.general.editorSection}
                >
                  <SettingsPage.SettingsRow
                    control={
                      <AssetLocationSelect
                        onValueChange={setAssetLocationPreference}
                        value={assetLocationPreference}
                      />
                    }
                    description={
                      t.app.settings.general.assetLocationDescription
                    }
                    title={t.app.settings.general.assetLocationTitle}
                  />
                </SettingsPage.SettingsSection>
              </>
            ) : activePage === 'workspaces' ? (
              <WorkspacesSettingsPage />
            ) : activePage === 'account' ? (
              <AccountSettingsPage />
            ) : activePage === 'import-export' ? (
              <ImportExportSettingsPage />
            ) : (
              <RecoverySettingsPage />
            )}
          </SettingsPage.SettingsPageContent>
        </SettingsPage.SettingsPageLayout>
      </PageContentContainer>
    </>
  );
}

function ColorThemePicker({
  value,
  onValueChange,
}: {
  value: ColorThemePreference;
  onValueChange: (value: ColorThemePreference) => void;
}) {
  const lightThemes = COLOR_THEMES.filter((theme) => theme.mode === 'light');
  const darkThemes = COLOR_THEMES.filter((theme) => theme.mode === 'dark');

  return (
    <div
      aria-label={t.app.settings.general.colorThemeLabel}
      className="flex w-full max-w-md flex-col gap-3"
      role="listbox"
    >
      <ThemeSwatchGroup
        label={t.app.settings.general.colorThemeLight}
        onValueChange={onValueChange}
        themes={lightThemes}
        value={value}
      />
      <ThemeSwatchGroup
        label={t.app.settings.general.colorThemeDark}
        onValueChange={onValueChange}
        themes={darkThemes}
        value={value}
      />
    </div>
  );
}

function ThemeSwatchGroup({
  label,
  themes,
  value,
  onValueChange,
}: {
  label: string;
  themes: readonly (typeof COLOR_THEMES)[number][];
  value: ColorThemePreference;
  onValueChange: (value: ColorThemePreference) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-muted-foreground text-xs">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {themes.map((theme) => {
          const selected = theme.id === value;
          return (
            <button
              aria-label={theme.label}
              aria-selected={selected}
              className={cn(
                'flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-left text-xs transition-colors',
                selected
                  ? 'border-primary bg-card shadow-sm'
                  : 'border-border bg-background hover:bg-muted',
              )}
              key={theme.id}
              onClick={() => {
                if (isColorThemePreference(theme.id)) {
                  onValueChange(theme.id);
                }
              }}
              role="option"
              type="button"
            >
              <span
                aria-hidden
                className="inline-flex h-4 w-4 overflow-hidden rounded-sm border border-border/60"
              >
                {theme.swatch.map((color) => (
                  <span
                    className="h-full flex-1"
                    key={color}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </span>
              <span>{theme.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EditorFontSelect({
  value,
  onValueChange,
}: {
  value: EditorFontPreference;
  onValueChange: (value: EditorFontPreference) => void;
}) {
  return (
    <Select
      items={EDITOR_FONT_ITEMS}
      onValueChange={(nextValue) => {
        if (isEditorFontPreference(nextValue)) {
          onValueChange(nextValue);
        }
      }}
      value={value}
    >
      <SelectTrigger
        aria-label={t.app.settings.general.editorFontLabel}
        className="w-full sm:w-52"
        style={{ fontFamily: EDITOR_FONT_FAMILY[value] }}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent
        align="end"
        alignItemWithTrigger={false}
        className="min-w-48"
      >
        {EDITOR_FONT_OPTIONS.map((option) => (
          <SelectItem
            key={option.value}
            style={{ fontFamily: EDITOR_FONT_FAMILY[option.value] }}
            value={option.value}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function AssetLocationSelect({
  value,
  onValueChange,
}: {
  value: AssetLocationPreference;
  onValueChange: (value: AssetLocationPreference) => void;
}) {
  return (
    <Select
      items={ASSET_LOCATION_ITEMS}
      onValueChange={(nextValue) => {
        if (isAssetLocationPreference(nextValue)) {
          onValueChange(nextValue);
        }
      }}
      value={value}
    >
      <SelectTrigger
        aria-label={t.app.settings.general.assetLocationTitle}
        className="w-full sm:w-48"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end" className="min-w-44">
        {ASSET_LOCATION_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const COMMENT_FONT_FAMILY: Record<CommentFontPreference, string> = {
  metafors: "'Metafors', cursive",
  another: "'Another', cursive",
  'cute-font': "'Cute_Font', cursive",
  'lemon-tuesday': "'Lemon Tuesday', cursive",
  'marker-sd': "'Marker SD', cursive",
};

function CommentFontSelect({
  value,
  onValueChange,
}: {
  value: CommentFontPreference;
  onValueChange: (value: CommentFontPreference) => void;
}) {
  return (
    <Select
      items={COMMENT_FONT_ITEMS}
      onValueChange={(nextValue) => {
        if (isCommentFontPreference(nextValue)) {
          onValueChange(nextValue);
        }
      }}
      value={value}
    >
      <SelectTrigger
        aria-label={t.app.settings.general.commentFontLabel}
        className="h-10 w-full text-xl data-[size=default]:h-10 sm:w-52"
        style={{ fontFamily: COMMENT_FONT_FAMILY[value] }}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent
        align="end"
        alignItemWithTrigger={false}
        className="min-w-52"
      >
        {COMMENT_FONT_OPTIONS.map((option) => (
          <SelectItem
            className="py-2 text-xl"
            key={option.value}
            style={{ fontFamily: COMMENT_FONT_FAMILY[option.value] }}
            value={option.value}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
